import { List, OrderedSet, Map } from 'immutable';
import { checkStatus, isFetched, addToFetched, generateAuthToken } from '../helper.js';
import { FETCHED, GEO_LIST_TREE_STRUCTURE} from '../../constants.js';
import { USPrivacyRegulation,RegulationListError, SystemPurpose, SystemVendor } from '../../records/us_privacy_regulation_records.js';

const permissionsSvcBaseUrl = process.env.REACT_APP_PERMISSIONS_API_URL;

const getBaseUrl = (isGlobalStandardFlow) => {
    return `${permissionsSvcBaseUrl}/${isGlobalStandardFlow ? "global-cmp" : "usnat"}/`;
};

async function checkVendorListStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else if (response.status === 400) {
    const errors = await response.json();
    throw new List(errors).map(err => new RegulationListError(err));
  } else {
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
}

export function getAccountsListAjax(isGlobalStandardFlow) {
    const url = 'vendor-list/account-list';
    // cant cache now as the endpoint route is dependent on isglobalStandardFlow. Will revisit this later if there is significant performance issue
    // if (isFetched(url)) {
    //   return Promise.resolve(FETCHED);
    // } else {
      return fetch(getBaseUrl(isGlobalStandardFlow) + url, {
        credentials: 'include',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
        .then(checkStatus)
        .then(resp => resp.json())
        .then(resp => {
          addToFetched(url);
          return List(resp.map(s => new USPrivacyRegulation(s) ));
        });
    // }
}

export function getUSPrivacyRegulationAjax(isGlobalStandardFlow, id) {
  const url = `vendor-list/v2?vendorListId=${id}`;
  return fetch(getBaseUrl(isGlobalStandardFlow) + url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(resp => resp.json())
    .then(resp => {
      resp.categories = resp.categories?.map(cat => {
        if(cat.type == 'SYSTEM_STACK' && !cat.vendorCategorization?.length){
          cat.vendorCategorization = cat.categories[0]?.vendorCategorization ?? {};
        }
        return cat;
      })
      return new USPrivacyRegulation(resp);
    });
}

export function cloneRegulationListAjax(isGlobalStandardFlow, accountId, vendorListId, cloneName) {
  return fetch(getBaseUrl(isGlobalStandardFlow) + `vendor-list/clone?vendorListId=${vendorListId}`, {
    method: 'POST',
    body: JSON.stringify({ name : cloneName}),
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(checkVendorListStatus)
    .then(resp => resp.json())
    .then(resp => {
      return new USPrivacyRegulation(resp);
    });
}

export function deleteRegulationAjax(isGlobalStandardFlow, accountId, id) {
  return fetch(getBaseUrl(isGlobalStandardFlow) + `vendor-list?accountId=${accountId}&vendorListId=${id}`, {
    method: 'DELETE',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(checkVendorListStatus)
    .then(resp => resp.json())
    .then(resp => {
      return new USPrivacyRegulation(resp);
    });
}

export function activateDeactivateRegulationAjax(isGlobalStandardFlow, regulation) {
  return fetch(getBaseUrl(isGlobalStandardFlow) + `vendor-list/active`, {
    method: 'PUT',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ "_id": regulation.id,"isActive": !regulation.isActive})})
    .then(checkVendorListStatus)
    .then(resp => resp.json())
    .then(resp => {
      return new USPrivacyRegulation(resp);
    });
}

//purposes and stacks
export function getSystemPurposesAjax(isGlobalStandardFlow, filterSysIds) {
  const url = `admin/system_purposes`;
  return fetch(getBaseUrl(isGlobalStandardFlow) + url, {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(resp => resp.json())
    .then(resp => {
      let sysPurposes = resp;
      if(filterSysIds) {
        sysPurposes = sysPurposes.filter((pur) => !filterSysIds.includes(pur.systemId));
      }
      return List(sysPurposes.map(p => new SystemPurpose(p).set('purposeRef',{ id : p._id, systemId: p.systemId})));
    });
}

export function getSystemStacksAjax(isGlobalStandardFlow, systemPurposes) {
  const url = 'admin/system_stacks';
  // if (isFetched(url)) {
  //   return Promise.resolve(FETCHED);
  // } else {
    return fetch(getBaseUrl(isGlobalStandardFlow) + url, {
      credentials: 'include',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(checkStatus)
      .then(resp => resp.json())
      .then(resp => {
        addToFetched(url);
        return List(resp.map(s => {
          return new SystemPurpose(s)
            .set('type', 'SYSTEM_STACK')
            .set('systemStackRef', {id: s._id, systemId: s.systemId})
            .set('defaultLegalBasis', 'CONSENT')
            // .set('iabStackRef', s._id)
            .delete('id')
            .set('categories', List(s.systemPurposes.map(pId => {
              const purpose = systemPurposes.find(purpose => purpose.purposeRef.id == pId)
              return purpose.set('purposeRef', {id: purpose.id, systemId: purpose.systemId}).set('defaultLegalBasis', 'CONSENT').delete('id')
            }))
              .set('id', null)
            )
        })
        )
      });
  // }
}

export function getStateSupportRegionsAjax(isGlobalStandardFlow, filterSysIds) {
  const url = `admin/state-support`;
  return fetch(getBaseUrl(isGlobalStandardFlow) + url, {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(resp => resp.json())
    .then(resp => {
      return resp;
    });
}

export function createUSPrivacyRegulationAjax(isGlobalStandardFlow, accountId, list) {
  return fetch(getBaseUrl(isGlobalStandardFlow) + 'vendor-list', {
    method: 'POST',
    body: JSON.stringify(generateRegulationData(accountId, list, isGlobalStandardFlow)),
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(checkVendorListStatus)
    .then(resp => resp.json())
    .then(resp => {
      const updatedResp = Object.assign({}, resp);
      // updatedResp.clientSideMessageId = list.clientSideMessageId;
      return new USPrivacyRegulation(updatedResp);
    });
}

export function updateUSPrivacyRegulationAjax(isGlobalStandardFlow, accountId, list) {
  return fetch(getBaseUrl(isGlobalStandardFlow) + 'vendor-list', {
    method: 'PUT',
    body: JSON.stringify(generateRegulationData(accountId, list, isGlobalStandardFlow)),
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(checkVendorListStatus)
    .then(resp => { return resp.json() })
    .then(resp => {
      resp.categories = resp.categories?.map(cat => {
        if(cat.type == 'SYSTEM_STACK' && !cat.vendorCategorization?.length){
          cat.vendorCategorization = cat.categories[0]?.vendorCategorization ?? {};
        }
        return cat;
      })
      return new USPrivacyRegulation(resp);
    });
}

export function verifyMSPASinatoryId(isGlobalStandardFlow, signatoryId) {
  const url = 'admin/mspa-signatory-list';
  const queryParams = `?signatoryId=${signatoryId}`
  return fetch(getBaseUrl(isGlobalStandardFlow) + url + queryParams, {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(checkStatus)
    .then(resp => resp.json())
}

export function getGlobalVendorsAjax(isGlobalStandardFlow) {
  const url = 'admin/system_vendors'
  //  if (isFetched(url)) {
  //   return Promise.resolve(FETCHED);
  // } else {
    return fetch(getBaseUrl(isGlobalStandardFlow) + url, {
      credentials: 'include',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(checkStatus)
      .then(resp => resp.json())
      .then(resp => {
        addToFetched(url);
        let filteredVendors = resp;
        // If the flow is global standard, filter out custom vendors that are also IAB vendors
        if (isGlobalStandardFlow) {
          const customVendorNames = new Set(
          resp.filter(v => v.vendorType === "CUSTOM").map(v => v.name)
          );
          filteredVendors = resp.filter(
          v => !(v.vendorType === "IAB" && customVendorNames.has(v.name))
          );
        }
        return OrderedSet(filteredVendors.map(vendor => new SystemVendor(vendor)));
      });
    // } 
}
function generateRegulationData(accountId, list, isGlobalStandardFlow) {
  const categories = list.categories?.map(c => {
    if(c.get("id")?.indexOf("custom_") === 0){
      c = c.set("id", null);
    }
    let id = c.id;
    if (c.systemPurposes.includes(c.id)) {
      id = null;
    }
    let category = c.toMap().delete('systemId')/*.set('vendors', c.vendorIds).delete('vendorIds')*/.delete('id').delete('systemPurposes').delete("disclosureOnly");
    if (category.get('purposeRef')) category = category.set('purposeRef', category.get('purposeRef').id);
    if (category.get('systemStackRef')) category = category.set('systemStackRef', category.get('systemStackRef').id);
    // if (!category.get('iabStackRef')) category = category.delete('iabStackRef');
    if (!category.get('translations')) category = category.delete('translations');

    if(typeof category.get('respectGPC') === 'undefined') category = category.set('respectGPC', null);

    if (category.get('categories') && Object.keys(category.get('categories'))?.length) {
      const updatedCategories = category.get('categories').map(c => {
        //before save comes as a string
        c = c.toMap().delete('systemId')/*.set('vendors', c.vendorIds).delete('vendorIds')*/.delete('systemPurposes');
        if (c.get('id')) {
          c = c.toMap().set('_id', c.get('id')).delete('id');
          if(c.get("_id").indexOf("custom_") === 0){
            c = c.delete("_id");
          }
        } else {
          c = c.toMap().delete('id').delete('_id');
        }
        if (c.get('purposeRef')) c = c.set('purposeRef', c.get('purposeRef').id);

        //https://sourcepoint.atlassian.net/browse/DIA-3304 update sub Cats with latest vendorCategorization mapping which is available in SYSTEM_STACKS category
        const stackVendorCatsMap = _.cloneDeep(category.get('vendorCategorization'));
        Object.values(stackVendorCatsMap).forEach(vCat => {delete vCat._id});
        const stackVendorCatsArray = Object.values(stackVendorCatsMap);
        if(c.get('vendorCategorization') && Object.keys(c.get('vendorCategorization'))?.length){
          let updatedSubCatVendorCatsMap = c.get('vendorCategorization');
          Object.keys(updatedSubCatVendorCatsMap).forEach(key => {
            if(!stackVendorCatsMap[key]){
              delete updatedSubCatVendorCatsMap[key];
            }
          })
          stackVendorCatsArray.forEach(vCat => {
            if(!updatedSubCatVendorCatsMap[vCat.vendorId]){
              updatedSubCatVendorCatsMap[vCat.vendorId] = vCat;
            }
          })
          c = c.set('vendorCategorization', Object.values(updatedSubCatVendorCatsMap));
        } else{
          c = c.set('vendorCategorization', stackVendorCatsArray);
        } 
        return c
      })
      category = category.set('categories', updatedCategories)
    }

    if(category.get('vendorCategorization')){
      if(category.get('type') == 'SYSTEM_STACK'){
        category = category.set('vendorCategorization', [])
      } else {
        category = category.set('vendorCategorization', Object.values(category.get('vendorCategorization')))
      }
    }
    // if(list.legislation === "state") {
      category = category.delete("defaultLegalBasis");
      if(category.get("configByGeo")) {
        category = category.update("configByGeo", (configByGeo) => Object.fromEntries(
          Object.entries(configByGeo).map(([key,value]) => {
            if(value.defaultLegalBasis === "DISCLOSURE_ONLY") {
              value.defaultLegalBasis = "OPT-OUT";
              value["disclosureOnly"] = true
            } else if(value.defaultLegalBasis === "NOT_APPLICABLE") {
              value.defaultLegalBasis = null;
            }
            return [key,value]
          })
        ))
      }
    // } else {
      // category = category.delete("configByGeo")
    // }
 
    if (id) category = category.set('_id', id);
    return category;
  });

  const vendorsWrappers = list.vendorsWrappers
    ? list.vendorsWrappers
        .map((vw) => {
          const id = vw.id;
          let updatedVendorWrapper = vw.toMap().delete("id");
          if (id) {
            updatedVendorWrapper = updatedVendorWrapper.set("_id", id);
          }
          const vendorId = updatedVendorWrapper.get("vendorId")
          if(vendorId) {
            updatedVendorWrapper = updatedVendorWrapper.set("vendor", vendorId) // vendor: Types.ObjectId | IVendorSchema; (backend)
            updatedVendorWrapper = updatedVendorWrapper.delete("vendorId");
          }
          updatedVendorWrapper = updatedVendorWrapper.update(
            "urlMappingRules",
            (mappings) => mappings.map((patterns) => ({ patterns }))
          );
          return updatedVendorWrapper;
        })
        .toJS()
    : [];
  
  //DIA-5802 from enumerated regions/states to just the country if every state/region is using the “group” consent scope
  const configByGeo = { ...list.configByGeo }
  GEO_LIST_TREE_STRUCTURE.forEach((geo) => {
    if(geo.code === "US" && geo.children.every((state) => configByGeo[state.code.replace("STATE_", "US_")]?.legislation === "usnat")) {
      geo.children.forEach((state) => {
        delete configByGeo[state.code.replace("STATE_", "US_")];
      });
      configByGeo["US"] = {legislation: "usnat"};
    }
    if(geo.code === "CA" && geo.children.every((province) => configByGeo[province.code]?.legislation === "usnat")) {
      geo.children.forEach((province) => {
        delete configByGeo[province.code];
      });
      configByGeo["CA"] = {legislation: "usnat"};
    }
  })

  if(isGlobalStandardFlow && list.metaData?.USNatVersion) {
    delete list.metaData.USNatVersion
  }
  
  const data = {
    accountId: accountId.toString(),
    name: list.name,
    description: list.description,
    // vendors: list.vendors.map(v => v.id).toSet().toJS(),
    categories,
    consentScope: list.consentScope,
    shareRootDomain: list.shareRootDomain,
    // hasConsentCompensation: list.hasConsentCompensation,
    // useSpecialTreatmentP1: list.useSpecialTreatmentP1,
    // specialTreatmentP1Geo: list.specialTreatmentP1Geo,
    siteIds: list.siteIds.toSet().toJS(),
    isActive: list.isActive,
    vendorsWrappers,
    autoUpdateVendorScan: list.autoUpdateVendorScan,
    // autoUpdateIabVendors2: list.allIabAndAutoUpdate,
    cookieMaxAge: list.cookieMaxAge,
    writeFirstPartyCookiesFromServer: list.writeFirstPartyCookiesFromServer,
    type: list.type,
    defaultLegalBasis: list.defaultLegalBasis,
    // publisherCmpId: list.publisherCmpId,
    // publisherCmpId2: list.publisherCmpId2,
    // iabSpecialFeatures: (list.iabSpecialFeatures && list.iabSpecialFeatures.size)
    //   ? list.iabSpecialFeatures.toJS()
    //   : list.iabSpecialFeatures,
    // displaySpecialPurposesNFeatures: list.displaySpecialPurposesNFeatures,
    // customElementsFirst: list.customElementsFirst,
    appliesGeos: list.appliesGeos.map(geo => geo === 'CAN' ? 'CA' : geo),
    exclusionGeos: list.exclusionGeos.map(geo => geo === 'CAN' ? 'CA' : geo),
    // useVendorActionsV2: list.useVendorActionsV2,
    // publisherPurposes: list.publisherPurposes,
    // setEuconsentCookie: list.setEuconsentCookie,
    // vendorScanCategorizations: list.vendorScanCategorizations,
    // onlyCustom: list.onlyCustom,
    // includeDisclosedVendors: list.includeDisclosedVendors,
    // noServerStorage: list.noServerStorage,
    // noUniqueIdReporting:list.noUniqueIdReporting,
    signatoryId: list.signatoryId,
    legislation: list.legislation,
    metaData: list.metaData,
    nonIab: list.nonIab,
    configByGeo: list.legislation === "hybrid" ? configByGeo : {}
  };

  if (list.id) {
    data._id = list.id;
  }

  return data;
}